import { useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { z } from "zod";
import { toast } from "sonner";
import {
  Bot,
  ChevronsUpDown,
  Copy,
  Info,
  Loader2,
  PenLine,
  RotateCw,
  Search,
  Sparkles,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Page, PageHeader } from "@/components/common/page";
import {
  Chip,
  CopyableValue,
  GuidanceList,
  GuidanceProse,
  JsonDisclosure,
  SectionHeading,
} from "@/components/intents/guidance-primitives";
import { GuidanceDoc } from "@/components/intents/guidance-doc";
import { ReferencedTools, ToolsRegistry } from "@/components/intents/tools";
import {
  CatalogMigrationBrowser,
  RerunMigrationButton,
} from "@/components/intents/catalog-migration";
import { cn } from "@/lib/utils";
import { formatDateTime } from "@/lib/date-utils";

import { useAllTenants } from "@/hooks/use-tenants";
import { CellTenantsUnavailable } from "@/components/cells/cell-failures-alert";
import { tenantLabel, tenantOptionValue } from "@/lib/cells/cells";
import { useDesks } from "@/hooks/use-desk-migration";
import {
  useHubIntent,
  useHubIntents,
  useRuntimeIntents,
  useTools,
  type HubIntentStatus,
  type RuntimeIntent,
} from "@/hooks/use-intent-hub";

const searchSchema = z.object({
  tenantId: z.string().uuid().optional().catch(undefined),
  tab: z.enum(["intents", "tools", "migration"]).optional().catch(undefined),
});

export const Route = createFileRoute("/intent-hub")({
  component: IntentHubPage,
  validateSearch: zodValidator(searchSchema),
});

type StatusFilter = "all" | HubIntentStatus;
type Tab = NonNullable<z.infer<typeof searchSchema>["tab"]>;

function StatusDot({ status }: { status: HubIntentStatus }) {
  return (
    <span
      aria-hidden
      className={cn(
        "inline-block size-1.5 shrink-0 rounded-full",
        status === "active" ? "bg-emerald-500" : "bg-muted-foreground/50",
      )}
    />
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="min-w-0">
      <dt className="text-muted-foreground text-[10px] font-semibold uppercase tracking-widest">
        {label}
      </dt>
      <dd className="text-xs leading-6">{children}</dd>
    </div>
  );
}

function IntentHubPage() {
  const { tenantId, tab = "intents" } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const [tenantOpen, setTenantOpen] = useState(false);

  const {
    data: tenants,
    isLoading: tenantsLoading,
    failures: tenantFailures,
  } = useAllTenants();
  const sortedTenants = useMemo(
    () =>
      [...(tenants ?? [])].sort((a, b) => a.company.localeCompare(b.company)),
    [tenants],
  );
  const selectedTenant = tenants?.find((t) => t.id === tenantId);

  return (
    <Page className="flex h-full flex-col">
      <PageHeader>
        <div className="flex-1 space-y-0.5">
          <p className="text-muted-foreground text-[10px] font-semibold uppercase tracking-[0.2em]">
            Backoffice · SDA
          </p>
          <h2 className="font-display text-3xl font-bold tracking-tight">
            Intents
          </h2>
        </div>
        {tab === "migration" && tenantId && (
          <RerunMigrationButton tenantId={tenantId} />
        )}
        <Popover open={tenantOpen} onOpenChange={setTenantOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={tenantOpen}
              className="w-[260px] justify-between"
            >
              <span className="truncate">
                {selectedTenant
                  ? tenantLabel(selectedTenant)
                  : (tenantId ?? "Select a tenant...")}
              </span>
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[260px] p-0" align="end">
            <Command>
              <CellTenantsUnavailable failures={tenantFailures} />
              <CommandInput placeholder="Search tenants..." />
              <CommandList>
                <CommandEmpty>
                  {tenantsLoading ? "Loading..." : "No tenant found."}
                </CommandEmpty>
                <CommandGroup>
                  {sortedTenants.map((tenant) => (
                    <CommandItem
                      key={tenant.id}
                      value={tenantOptionValue(tenant)}
                      onSelect={() => {
                        void navigate({
                          search: { tenantId: tenant.id, tab },
                        });
                        setTenantOpen(false);
                      }}
                      className={cn(tenantId === tenant.id && "bg-accent")}
                    >
                      {tenantLabel(tenant)}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </PageHeader>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <Tabs
          value={tab}
          onValueChange={(v) => {
            void navigate({
              search: { tenantId, tab: v as Tab },
            });
          }}
        >
          <TabsList>
            <TabsTrigger value="intents">Intents</TabsTrigger>
            <TabsTrigger value="tools">Tools registry</TabsTrigger>
            <TabsTrigger value="migration">Catalog migration</TabsTrigger>
          </TabsList>
        </Tabs>
        {tab === "migration" ? (
          <p className="text-muted-foreground flex items-center gap-2 border-l-2 border-amber-400/80 py-1 pl-3 text-[13px]">
            <Info className="size-3.5 shrink-0 text-amber-500" />
            Migration phase only — reviews and activates the catalog-derived
            intents (a separate entity from the Intents tab) and goes away once
            intents are managed in the product. Offered only to tenants with the
            sda-catalog-intents flag.
          </p>
        ) : (
          <p className="text-muted-foreground flex items-center gap-2 border-l-2 border-sky-400/80 py-1 pl-3 text-[13px]">
            <Info className="size-3.5 shrink-0 text-sky-500" />
            {tab === "tools"
              ? "Every tool an intent can reference. The registry is tenant-independent; per-tenant availability is not evaluated."
              : "Read-only review of a tenant's intents: the authored guidance next to the instruction text the agent actually receives."}
          </p>
        )}
      </div>

      {!tenantId ? (
        <div className="border-muted-foreground/25 text-muted-foreground flex h-64 flex-col items-center justify-center gap-3 rounded-xl border border-dashed text-sm">
          <span className="border-muted-foreground/30 flex size-12 items-center justify-center rounded-full border border-dashed">
            <Info className="size-5 opacity-60" />
          </span>
          Select a tenant to review its intents.
        </div>
      ) : tab === "tools" ? (
        <ToolsRegistry key={tenantId} tenantId={tenantId} />
      ) : tab === "migration" ? (
        <CatalogMigrationBrowser key={tenantId} tenantId={tenantId} />
      ) : (
        <IntentsBrowser key={tenantId} tenantId={tenantId} />
      )}
    </Page>
  );
}

function IntentsBrowser({ tenantId }: { tenantId: string }) {
  const {
    data: intents,
    isLoading,
    isLoadingError,
    isError,
    isFetching,
    refetch,
  } = useHubIntents(tenantId);
  const tools = useTools(tenantId);
  const queryClient = useQueryClient();
  const { data: desks } = useDesks(tenantId);
  const detailRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Derived from the unfiltered list so a filter change keeps the open
  // detail, while a refresh that dropped the intent closes it.
  const selected = intents?.find((i) => i.id === selectedId) ?? null;
  const runtime = useRuntimeIntents(tenantId, selected?.status);

  const deskNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const desk of desks ?? []) map.set(desk.id, desk.name);
    return map;
  }, [desks]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (intents ?? [])
      .filter((i) => statusFilter === "all" || i.status === statusFilter)
      .filter((i) => !q || i.name.toLowerCase().includes(q))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [intents, query, statusFilter]);

  const counts = useMemo(() => {
    const list = intents ?? [];
    const active = list.filter((i) => i.status === "active").length;
    return { all: list.length, active, paused: list.length - active };
  }, [intents]);

  if (isLoading) {
    return (
      <div
        role="status"
        aria-label="Loading intents"
        className="flex h-64 items-center justify-center"
      >
        <Loader2 className="text-muted-foreground h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (isLoadingError) {
    return (
      <div
        role="alert"
        className="border-destructive/30 text-muted-foreground flex h-56 flex-col items-center justify-center gap-3 rounded-xl border border-dashed text-sm"
      >
        Failed to load intents. Is an intents-api with the /intents routes
        deployed for this environment?
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            void refetch();
          }}
        >
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 lg:grid-cols-[340px_1fr]">
      <div className="bg-card flex min-h-0 flex-col overflow-hidden rounded-xl border">
        <div className="flex flex-col gap-2 border-b p-2">
          {isError && (
            <p
              role="alert"
              className="border-destructive/40 bg-destructive/5 text-destructive rounded-md border px-2.5 py-1.5 text-xs"
            >
              Refresh failed — showing the last successful load.
            </p>
          )}
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="text-muted-foreground pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2" />
              <Input
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                }}
                placeholder="Search intents…"
                className="pl-8"
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              className="h-9 shrink-0 px-2.5"
              aria-label="Refresh intents"
              disabled={isFetching}
              onClick={() => {
                // Covers the list, every open detail and the runtime read.
                void queryClient.invalidateQueries({
                  queryKey: ["intent-hub", tenantId],
                });
              }}
            >
              <RotateCw
                className={cn("size-4", isFetching && "animate-spin")}
              />
            </Button>
          </div>
          <Tabs
            value={statusFilter}
            onValueChange={(v) => {
              setStatusFilter(v as StatusFilter);
            }}
          >
            <TabsList className="w-full">
              <TabsTrigger value="all">All ({counts.all})</TabsTrigger>
              <TabsTrigger value="active">Active ({counts.active})</TabsTrigger>
              <TabsTrigger value="paused">Paused ({counts.paused})</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-2">
          {filtered.length === 0 ? (
            <p className="text-muted-foreground p-4 text-center text-sm">
              {counts.all === 0
                ? "This tenant has no intents yet."
                : "No intent matches the filter."}
            </p>
          ) : (
            <ul className="flex flex-col gap-0.5">
              {filtered.map((intent) => {
                const isSelected = intent.id === selectedId;
                return (
                  <li key={intent.id}>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedId(intent.id);
                        requestAnimationFrame(() => {
                          detailRef.current?.focus();
                        });
                      }}
                      aria-current={isSelected}
                      className={cn(
                        "focus-visible:ring-ring relative flex w-full items-center gap-2.5 rounded-lg py-2 pl-3.5 pr-2.5 text-left text-sm transition-colors focus-visible:outline-none focus-visible:ring-2",
                        isSelected
                          ? "bg-accent text-accent-foreground font-medium"
                          : "hover:bg-accent/60",
                      )}
                    >
                      <span
                        aria-hidden
                        className={cn(
                          "bg-primary absolute inset-y-1.5 left-0 w-0.5 rounded-full transition-opacity",
                          isSelected ? "opacity-100" : "opacity-0",
                        )}
                      />
                      <StatusDot status={intent.status} />
                      <span className="sr-only">{intent.status}</span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate">{intent.name}</span>
                        <span className="text-muted-foreground block truncate text-xs font-normal">
                          {deskNames.get(intent.desk_id) ?? intent.desk_id}
                          {" · "}
                          {intent.type}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      <div
        ref={detailRef}
        tabIndex={-1}
        role="region"
        aria-label="Intent details"
        className="bg-card min-h-0 overflow-y-auto rounded-xl border focus:outline-none"
      >
        {selected ? (
          <IntentDetail
            key={selected.id}
            tenantId={tenantId}
            intentId={selected.id}
            deskNames={deskNames}
            runtime={runtime.data?.get(selected.id)}
            runtimeLoading={runtime.isLoading}
            runtimeError={runtime.isError}
            tools={tools}
          />
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-3 p-12 text-center">
            <span className="border-muted-foreground/30 flex size-12 items-center justify-center rounded-full border border-dashed">
              <Sparkles className="text-muted-foreground size-5" />
            </span>
            <p className="text-sm font-medium">Select an intent</p>
            <p className="text-muted-foreground max-w-sm text-sm">
              See the guidance as authored and as the agent reads it.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Detail                                                              */
/* ------------------------------------------------------------------ */

function CopyButton({ text, label }: { text: string; label: string }) {
  return (
    <Button
      variant="ghost"
      size="sm"
      className="h-6 px-1.5 text-xs"
      onClick={() => {
        navigator.clipboard.writeText(text).then(
          () => toast.success(`${label} copied`),
          () => toast.error("Copy failed"),
        );
      }}
    >
      <Copy className="mr-1 size-3" />
      Copy
    </Button>
  );
}

function AgentView({
  runtime,
  isLoading,
  isError,
}: {
  runtime: RuntimeIntent | undefined;
  isLoading: boolean;
  isError: boolean;
}) {
  if (isLoading) {
    return (
      <div
        role="status"
        aria-label="Loading agent view"
        className="flex h-32 items-center justify-center"
      >
        <Loader2 className="text-muted-foreground size-6 animate-spin" />
      </div>
    );
  }
  if (isError) {
    return (
      <p role="alert" className="text-destructive text-sm">
        The runtime read failed — is an intents-api with the internal intents
        route deployed for this environment?
      </p>
    );
  }
  if (!runtime) {
    return (
      <p className="text-muted-foreground text-sm">
        The runtime read leaves this intent out: its guidance document is
        missing or no longer parses, so the agent is never offered it (see the{" "}
        <span className="font-mono">intent.runtime.unrenderable</span> warning
        in intents-api).
      </p>
    );
  }
  return (
    <div className="space-y-4">
      {runtime.wrapper && (
        <GuidanceProse title="Wrapper" text={runtime.wrapper} />
      )}
      {runtime.clarification && (
        <GuidanceProse title="Clarification" text={runtime.clarification} />
      )}
      <section className="space-y-2">
        <SectionHeading
          note="to_instructions(guidance)"
          action={
            <CopyButton text={runtime.instructions} label="Instructions" />
          }
        >
          Instructions
        </SectionHeading>
        <pre className="bg-muted/30 text-foreground/85 overflow-x-auto whitespace-pre-wrap break-words rounded-lg border p-3 font-mono text-xs leading-relaxed">
          {runtime.instructions || (
            <span className="text-muted-foreground italic">(empty)</span>
          )}
        </pre>
      </section>
    </div>
  );
}

function IntentDetail({
  tenantId,
  intentId,
  deskNames,
  runtime,
  runtimeLoading,
  runtimeError,
  tools,
}: {
  tenantId: string;
  intentId: string;
  deskNames: Map<string, string>;
  runtime: RuntimeIntent | undefined;
  runtimeLoading: boolean;
  runtimeError: boolean;
  tools: ReturnType<typeof useTools>;
}) {
  const {
    data: intent,
    isLoading,
    isError,
    refetch,
  } = useHubIntent(tenantId, intentId);

  if (isLoading) {
    return (
      <div
        role="status"
        aria-label="Loading intent"
        className="flex h-64 items-center justify-center"
      >
        <Loader2 className="text-muted-foreground h-8 w-8 animate-spin" />
      </div>
    );
  }
  if (isError || !intent) {
    return (
      <div role="alert" className="space-y-2 p-6 text-sm">
        <p className="text-destructive">Failed to load the intent.</p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            void refetch();
          }}
        >
          Retry
        </Button>
      </div>
    );
  }

  const { config } = intent;
  const audience =
    config.audience_rules.target === "all"
      ? "Everyone"
      : `Departments: ${(config.audience_rules.departments ?? []).join(", ") || "—"}`;

  return (
    <div className="animate-in fade-in slide-in-from-bottom-1 flex flex-col duration-300">
      <div className="bg-card/95 sticky top-0 z-10 flex flex-wrap items-start justify-between gap-3 border-b px-6 py-4 backdrop-blur">
        <div className="min-w-0 space-y-1">
          <div className="flex items-center gap-2.5">
            <h2 className="font-display truncate text-xl font-semibold tracking-tight">
              {config.name}
            </h2>
            <Chip>
              <StatusDot status={intent.status} />
              {intent.status}
            </Chip>
            <Chip>{intent.type}</Chip>
          </div>
          <p className="text-muted-foreground text-xs">
            {deskNames.get(config.desk_id) ?? config.desk_id}
            {" · "}
            {audience}
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-6 p-6">
        <dl className="grid grid-cols-2 gap-x-6 gap-y-3 border-b pb-4 text-sm sm:grid-cols-4">
          <Field label="Intent ID">
            <span className="font-mono">
              <CopyableValue label="Intent ID" value={intent.id} />
            </span>
          </Field>
          <Field label="Row version">
            <span className="font-mono">{intent.row_version}</span>
          </Field>
          <Field label="Created">
            {formatDateTime(intent.created_at)}
            {intent.created_by && (
              <span className="text-muted-foreground">
                {" "}
                · {intent.created_by}
              </span>
            )}
          </Field>
          <Field label="Updated">
            {formatDateTime(intent.updated_at)}
            {intent.updated_by && (
              <span className="text-muted-foreground">
                {" "}
                · {intent.updated_by}
              </span>
            )}
          </Field>
        </dl>

        {config.description && (
          <GuidanceProse title="Description" text={config.description} />
        )}
        {config.when_to_use && (
          <GuidanceProse title="When to use" text={config.when_to_use} />
        )}
        <GuidanceList
          title="When not to use"
          items={config.when_not_to_use}
          tone="negative"
        />
        <GuidanceList title="Examples" items={config.examples} />

        <ReferencedTools
          tenantId={tenantId}
          references={intent.references}
          tools={tools}
        />

        <div className="grid gap-6 xl:grid-cols-2">
          <section className="flex min-w-0 flex-col gap-3 rounded-lg border p-4">
            <p className="text-muted-foreground flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.2em]">
              <PenLine className="size-3.5" />
              Builder view
            </p>
            {config.guidance ? (
              <GuidanceDoc guidance={config.guidance} />
            ) : (
              <p className="text-muted-foreground text-sm italic">
                No guidance authored.
              </p>
            )}
          </section>
          <section className="bg-muted/20 flex min-w-0 flex-col gap-3 rounded-lg border p-4">
            <p className="text-muted-foreground flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.2em]">
              <Bot className="size-3.5" />
              Agent view
            </p>
            <AgentView
              runtime={runtime}
              isLoading={runtimeLoading}
              isError={runtimeError}
            />
          </section>
        </div>

        {config.guidance && (
          <JsonDisclosure
            title="Stored guidance document"
            value={config.guidance}
          />
        )}
      </div>
    </div>
  );
}
